import toolbarStyle from "./toolbar.module.scss";

import icn_btn_export from "assets/images/icons/icn_btn_export.svg";
import { ReactComponent as Dropdown } from "assets/images/icons/icn_dropdown.svg";
import { ReactComponent as Pullup } from "assets/images/icons/icn_pullup.svg";
import { ReactComponent as Eye } from "assets/images/icons/icn_display_options.svg";
import { ReactComponent as EyeHover } from "assets/images/icons/icn_display_options_hover.svg";
import { ReactComponent as SelectedIcon } from "assets/images/icons/icn_selected.svg";
import { ReactComponent as UnselectedIcon } from "assets/images/icons/icn_unselected.svg";

import React, { useState, useEffect } from 'react';
import {
  Box,
  Tooltip,
  Typography,
  Stack,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { cloneDeep } from "lodash";

import {
  deviceStateOption, deviceStateDictionary,
  filterOption as defaultFilterOption,
  eyeOption as defaultEyeOption
} from "../const";
import {
  getDefaultButtonSx,
  getDefaultMenuSx,
  getDefaultTextFieldSx,
  getDefaultMenuItemSx,
  getDefaultTopologySx,
  getDefaultCheckboxSx
} from "../theme";

const Toolbar = ({ treeIndex, statistic, onSearch }) => {
  const theme = useTheme();

  // State
  const [searchAnchorEl, setSearchAnchorEl] = useState(null);
  const [modelAnchorEl, setModelAnchorEl] = useState(null);
  const [stateAnchorEl, setStateAnchorEl] = useState(null);
  const [tagAnchorEl, setTagAnchorEl] = useState(null);
  const [eyeAnchorEl, setEyeAnchorEl] = useState(null);

  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [isStateOpen, setIsStateOpen] = useState(false);
  const [isTagOpen, setIsTagOpen] = useState(false);
  const [isEyeOpen, setIsEyeOpen] = useState(false);
  const [isEyeHover, setIsEyeHover] = useState(false);
  const [filterOption, setFilterOption] = useState(cloneDeep(defaultFilterOption));
  const [eyeOption, setEyeOption] = useState(cloneDeep(defaultEyeOption));

  // Method
  const handleSearchMenuOpen = (event) => {
    setSearchAnchorEl(event.currentTarget);
    setIsSearchOpen(!isSearchOpen);
  };

  const handleSearchMenuClose = () => {
    setSearchAnchorEl(null);
    setIsSearchOpen(false);
  };

  const handleModelMenuOpen = (event) => {
    setModelAnchorEl(event.currentTarget);
    setIsModelOpen(!isModelOpen);
  }

  const handleModelMenuClose = () => {
    setModelAnchorEl(null);
    setIsModelOpen(false);
  }

  const handleStateMenuOpen = (event) => {
    setStateAnchorEl(event.currentTarget);
    setIsStateOpen(!isStateOpen);
  }

  const handleStateMenuClose = () => {
    setStateAnchorEl(null);
    setIsStateOpen(false);
  }

  const handleTagMenuOpen = (event) => {
    setTagAnchorEl(event.currentTarget);
    setIsTagOpen(!isTagOpen);
  }

  const handleTagMenuClose = () => {
    setTagAnchorEl(null);
    setIsTagOpen(false);
  }

  const findSelectedModel = () => filterOption.model.options.find(model => model.checked === true);
  const findSelectedState = () => filterOption.state.options.find(state => state.checked === true);
  const findSelectedTag = () => filterOption.tag.options.find(tag => tag.checked === true);

  const clearSearch = () => {
    const updatedFilterOption = cloneDeep(defaultFilterOption);

    updatedFilterOption.model.options = filterOption.model.options.map(model => {
      return {
        ...model,
        checked: false
      }
    });

    updatedFilterOption.state.options = filterOption.state.options.map(state => {
      return {
        ...state,
        checked: false
      }
    });

    updatedFilterOption.tag.options = filterOption.tag.options.map(tag => {
      return {
        ...tag,
        checked: false
      }
    });

    setFilterOption(updatedFilterOption);
    onSearch(treeIndex, updatedFilterOption, eyeOption);
  }

  const handleEyeMenuOpen = (event) => {
    setEyeAnchorEl(event.currentTarget);
    setIsEyeOpen(!isEyeOpen);
  }

  const handleEyeMenuClose = () => {
    setEyeAnchorEl(null);
    setIsEyeOpen(false);
  }

  const processSearch = (type, newOption) => {
    let updatedFilterOption = cloneDeep(filterOption);
    let updatedEyeOption = cloneDeep(eyeOption);

    if (type === "filter") {
      updatedFilterOption = newOption;
    } else {
      updatedEyeOption = newOption;
    }

    setFilterOption(updatedFilterOption);
    setEyeOption(updatedEyeOption);
    onSearch(treeIndex, updatedFilterOption, updatedEyeOption);
  }

  // Side effect
  useEffect(() => {
    const updatedFilterOption = cloneDeep(defaultFilterOption);

    // Fetch model options
    const updatedModelOptions = [
      ...statistic['models'].map(model => {
        return {
          label: model,
          value: model,
          checked: false
        }
      })
    ];
    updatedFilterOption.model.options = updatedModelOptions;

    // Fetch state options
    const updatedStateOptions = [];
    for (const key in deviceStateOption) {
      updatedStateOptions.push({
        icon: deviceStateDictionary[deviceStateOption[key]].icon,
        label: deviceStateDictionary[deviceStateOption[key]].label,
        value: deviceStateOption[key],
        checked: false
      });
    }
    updatedFilterOption.state.options = updatedStateOptions;

    // Fetch tag options
    const updatedTagOptions = [
      ...statistic['tags'].map(tag => {
        return {
          label: tag,
          value: tag,
          checked: false
        }
      }),
    ];

    updatedFilterOption.tag.options = updatedTagOptions;
    setFilterOption(updatedFilterOption);
  }, [statistic, statistic.models, statistic.states, statistic.tags]);

  return (
    <Box key={`topology-toolbar-${treeIndex}`} className={toolbarStyle['toolbar']}>
      {/* Statistic */}
      <Box className={toolbarStyle['statistic']}>
        <Box className={toolbarStyle['item']}>
          <Box className={toolbarStyle['label']}>Online</Box>
          <Tooltip
            arrow
            title={
              <Stack>
                <Box className={toolbarStyle['tip']}>
                  <Typography className={toolbarStyle['label']}>Access Point</Typography>
                  <Typography>{statistic.online.ap}</Typography>
                </Box>
                <Box className={toolbarStyle['tip']}>
                  <Typography className={toolbarStyle['label']}>Switch</Typography>
                  <Typography>{statistic.online.sw}</Typography>
                </Box>
                <Box className={toolbarStyle['tip']}>
                  <Typography className={toolbarStyle['label']}>Router</Typography>
                  <Typography>{statistic.online.rtr}</Typography>
                </Box>
              </Stack>
            }>
            <Box className={toolbarStyle['online-icon']}>{statistic.online.count}</Box>
          </Tooltip>
        </Box>
        <Box className={toolbarStyle['item']}>
          <Box className={toolbarStyle['label']}>Offline</Box>
          <Tooltip
            arrow
            title={
              <Stack>
                <Box className={toolbarStyle['tip']}>
                  <Typography className={toolbarStyle['label']}>Access Point</Typography>
                  <Typography>{statistic.offline.ap}</Typography>
                </Box>
                <Box className={toolbarStyle['tip']}>
                  <Typography className={toolbarStyle['label']}>Switch</Typography>
                  <Typography>{statistic.offline.sw}</Typography>
                </Box>
                <Box className={toolbarStyle['tip']}>
                  <Typography className={toolbarStyle['label']}>Router</Typography>
                  <Typography>{statistic.offline.rtr}</Typography>
                </Box>
              </Stack>
            }>
            <Box className={toolbarStyle['offline-icon']}>{statistic.offline.count}</Box>
          </Tooltip>
        </Box>
        {
          treeIndex !== -1 && (
            <>
              {/* Task: #2191, hide client information */}
              {/* <Box className={toolbarStyle['item']}>
                <Box className={toolbarStyle['label']}>Client (Wireless)</Box>
                <Box className={toolbarStyle['wireless-icon']}>{statistic.wireless.count}</Box>
              </Box>
              <Box className={toolbarStyle['item']}>
                <Box className={toolbarStyle['label']}>Client (Wired)</Box>
                <Box className={toolbarStyle['wired-icon']}>{statistic.wired.count}</Box>
              </Box> */}
            </>
          )
        }
      </Box>
      {/* Search */}
      <Box className={toolbarStyle['search']}>
        <Box style={{ width: '2px', height: '33px', backgroundColor: '#d5d5d5' }} />
        <Box>
          <Grid item xs>
            <Box
              sx={{
                ...getDefaultButtonSx(theme),
                width: "250px",
              }}
              onClick={handleSearchMenuOpen}
            >
              <div style={{ flex: 1, textAlign: "left", paddingRight: "8px" }}>
                Search by
              </div>
              <Box className={toolbarStyle['search-icon']} />
            </Box>
          </Grid>
          <Menu
            anchorEl={searchAnchorEl}
            open={Boolean(searchAnchorEl)}
            onClose={handleSearchMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right", }}
            transformOrigin={{ vertical: "top", horizontal: "right", }}
            sx={{
              transform: "translateX(2px)",
            }}
            slotProps={{
              paper: {
                sx: {
                  ...getDefaultMenuSx(theme),
                }
              }
            }}
          >
            {/* Keyword */}
            <Box sx={{ mb: 1 }}>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: "bold",
                  color: "#373737",
                  marginBottom: "8px",
                }}
              >
                Keyword
              </Typography>
              <TextField
                fullWidth
                value={filterOption.keyword}
                onChange={(e) => {
                  const updatedFilterOption = { ...filterOption, keyword: e.target.value };
                  processSearch("filter", updatedFilterOption);
                }}
                sx={{
                  ...getDefaultTextFieldSx(theme),
                  "& .MuiOutlinedInput-root": {
                    ...getDefaultTextFieldSx(theme)["& .MuiOutlinedInput-root"],
                    height: "29px",
                    borderRadius: "8px",
                    bgcolor: "#ffffff",
                  }
                }}
              />
            </Box>
            {/* Model */}
            <Box sx={{ mb: 1 }}>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: "bold",
                  color: "#373737",
                  marginBottom: "8px",
                }}
              >
                Model
              </Typography>
              <Box
                sx={{
                  ...getDefaultButtonSx(theme),
                  width: "100%",
                  height: "29px",
                  borderRadius: "8px",
                  bgcolor: "#ffffff",
                }}
                onClick={handleModelMenuOpen}
              >
                <div style={{ flex: 1, textAlign: "left", paddingRight: "8px" }}>
                  {findSelectedModel() ? findSelectedModel().label : ""}
                </div>
                {isModelOpen ? <Dropdown /> : <Pullup />}
              </Box>
              <Menu
                anchorEl={modelAnchorEl}
                open={Boolean(modelAnchorEl)}
                onClose={handleModelMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right", }}
                transformOrigin={{ vertical: "top", horizontal: "right", }}
                sx={{
                  transform: "translateX(-8px)",
                }}
                slotProps={{
                  paper: {
                    sx: {
                      ...getDefaultMenuSx(theme),
                      mt: 0,
                      width: "252px",
                      maxHeight: "300px",
                      overflowY: "auto",
                      "&::before": {
                        content: '""',
                      },
                    },
                  }
                }}
              >
                {
                  filterOption.model.options.map((model, index) => {
                    return (
                      <MenuItem
                        key={`model-${index}`}
                        sx={{
                          ...getDefaultMenuItemSx(theme),
                          marginTop: "8px",
                          bgcolor: model.checked ? "#dbe0fc" : "transparent",
                        }}
                        onClick={() => {
                          const updatedModelOptions = filterOption.model.options.map((model, i) => {
                            return {
                              ...model,
                              checked: index === i ? true : false
                            }
                          });
                          const updatedFilterOption = {
                            ...filterOption,
                            model: {
                              options: updatedModelOptions
                            }
                          };

                          processSearch("filter", updatedFilterOption);
                          handleModelMenuClose();
                        }}
                      >
                        {model.label}
                      </MenuItem>
                    );
                  })
                }
              </Menu>
            </Box>
            {/* State */}
            <Box sx={{ mb: 1 }}>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: "bold",
                  color: "#373737",
                  marginBottom: "8px",
                }}
              >
                State
              </Typography>
              <Box
                sx={{
                  ...getDefaultButtonSx(theme),
                  width: "100%",
                  height: "29px",
                  borderRadius: "8px",
                  bgcolor: "#ffffff",
                }}
                onClick={handleStateMenuOpen}
              >
                <div style={{ flex: 1, textAlign: "left", paddingRight: "8px" }}>
                  {
                    findSelectedState() ? (
                      <Box sx={{ display: "flex", alignItems: "center", "gap": "8px" }}>
                        <img src={findSelectedState().icon} alt={findSelectedState().label} />
                        <span>{findSelectedState().label}</span>
                      </Box>
                    ) : ""
                  }
                </div>
                {isStateOpen ? <Dropdown /> : <Pullup />}
              </Box>
              <Menu
                anchorEl={stateAnchorEl}
                open={Boolean(stateAnchorEl)}
                onClose={handleStateMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right", }}
                transformOrigin={{ vertical: "top", horizontal: "right", }}
                sx={{
                  transform: "translateX(-8px)",
                }}
                slotProps={{
                  paper: {
                    sx: {
                      ...getDefaultMenuSx(theme),
                      mt: 0,
                      width: "252px",
                      maxHeight: "300px",
                      overflowY: "auto",
                      "&::before": {
                        content: '""',
                      }
                    }
                  }
                }}
              >
                {
                  filterOption.state.options.map((state, index) => {
                    return (
                      <MenuItem
                        key={`state-${index}`}
                        sx={{
                          ...getDefaultMenuItemSx(theme),
                          marginTop: "8px",
                          bgcolor: state.checked ? "#dbe0fc" : "transparent",
                        }}
                        onClick={() => {
                          const updatedStateOptions = filterOption.state.options.map((state, i) => {
                            return {
                              ...state,
                              checked: index === i ? true : false
                            }
                          });

                          const updatedFilterOption = {
                            ...filterOption,
                            state: {
                              options: updatedStateOptions
                            }
                          };

                          processSearch("filter", updatedFilterOption);
                          handleStateMenuClose();
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", "gap": "8px" }}>
                          <img src={state.icon} alt={state.label} />
                          <span>{state.label}</span>
                        </Box>
                      </MenuItem>
                    );
                  })
                }
              </Menu>
            </Box>
            {/* Tag */}
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: "bold",
                  color: "#373737",
                  marginBottom: "8px",
                }}
              >
                Tag
              </Typography>
              <Box
                sx={{
                  ...getDefaultButtonSx(theme),
                  width: "100%",
                  height: "29px",
                  borderRadius: "8px",
                  bgcolor: "#ffffff",
                }}
                onClick={handleTagMenuOpen}
              >
                <div style={{ flex: 1, textAlign: "left", paddingRight: "8px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {findSelectedTag() ? findSelectedTag().label : ""}
                </div>
                {isTagOpen ? <Dropdown /> : <Pullup />}
              </Box>
              <Menu
                anchorEl={tagAnchorEl}
                open={Boolean(tagAnchorEl)}
                onClose={handleTagMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right", }}
                transformOrigin={{ vertical: "top", horizontal: "right", }}
                sx={{
                  transform: "translateX(-8px)",
                }}
                slotProps={{
                  paper: {
                    sx: {
                      ...getDefaultMenuSx(theme),
                      mt: 0,
                      width: "252px",
                      maxHeight: "300px",
                      overflowY: "auto",
                      "&::before": {
                        content: '""',
                      }
                    }
                  }
                }}
              >
                {
                  filterOption.tag.options.map((tag, index) => {
                    return (
                      <MenuItem
                        key={`tag-${index}`}
                        sx={{
                          ...getDefaultMenuItemSx(theme),
                          marginTop: "8px",
                          bgcolor: tag.checked ? "#dbe0fc" : "transparent",
                        }}
                        onClick={() => {
                          const updatedTagOptions = filterOption.tag.options.map((tag, i) => {
                            return {
                              ...tag,
                              checked: index === i ? true : false
                            }
                          });

                          const updatedFilterOption = {
                            ...filterOption,
                            tag: {
                              options: updatedTagOptions
                            }
                          };

                          processSearch("filter", updatedFilterOption);
                          handleTagMenuClose();
                        }}
                      >
                        <Box
                          sx={{
                            width: "100%",
                            minWidth: 0,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {tag.label}
                        </Box>
                      </MenuItem>
                    );
                  })
                }
              </Menu>

            </Box>
            {/* Clear button */}
            <Box
              sx={{ mt: 2, mb: 2, display: "flex", justifyContent: "center", alignItems: "center" }}
            >
              <div
                className={toolbarStyle['toolbar-btn']}
                style={{ width: 'auto' }}
                onClick={clearSearch}
              >
                <span className={toolbarStyle['text']}>Clear filter</span>
              </div>
            </Box>
          </Menu>
        </Box>
        {/* Eye */}
        <Box>
          <IconButton
            sx={{
              "&:hover": {
                bgcolor: "transparent",
              },
            }}
            onMouseEnter={() => setIsEyeHover(true)}
            onMouseLeave={() => setIsEyeHover(false)}
            onClick={handleEyeMenuOpen}
          >
            <Box>
              {isEyeHover ? <EyeHover className={toolbarStyle['icon']} /> : <Eye className={toolbarStyle['icon']} />}
            </Box>
          </IconButton>
          <Menu
            anchorEl={eyeAnchorEl}
            open={Boolean(eyeAnchorEl)}
            onClose={handleEyeMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right", }}
            transformOrigin={{ vertical: "top", horizontal: "right", }}
            slotProps={{
              paper: {
                sx: {
                  ...getDefaultMenuSx(theme),
                  width: "300px",
                },
              }
            }}
          >
            <Box style={{ paddingLeft: '20px' }}>
              <FormGroup>
                {/* Description */}
                <Typography sx={getDefaultTopologySx()}>Description</Typography>
                <FormControlLabel
                  sx={{ transform: "translateX(-4px)", }}
                  control={
                    <Checkbox
                      icon={<UnselectedIcon />}
                      checkedIcon={<SelectedIcon />}
                      checked={eyeOption.deviceName}
                      onChange={(e) => {
                        const updatedEyeOption = { ...eyeOption, deviceName: e.target.checked };
                        processSearch("eye", updatedEyeOption);
                      }}
                      name="deviceName"
                      sx={getDefaultCheckboxSx()}
                    />
                  }
                  label="Device Name"
                />
                {
                  treeIndex !== -1 && (
                    <>
                      {/* Task: #2191, hide client information */}
                      {/* <FormControlLabel
                        sx={{ transform: "translateX(-4px)", }}
                        control={
                          <Checkbox
                            icon={<UnselectedIcon />}
                            checkedIcon={<SelectedIcon />}
                            checked={eyeOption.clientName}
                            onChange={(e) => {
                              const updatedEyeOption = { ...eyeOption, clientName: e.target.checked };
                              processSearch("eye", updatedEyeOption);
                            }}
                            name="clientName"
                            sx={getDefaultCheckboxSx()}
                          />
                        }
                        label="Client Name"
                      /> */}
                    </>
                  )
                }
                <FormControlLabel
                  sx={{ transform: "translateX(-4px)", }}
                  control={
                    <Checkbox
                      icon={<UnselectedIcon />}
                      checkedIcon={<SelectedIcon />}
                      checked={eyeOption.macAddress}
                      onChange={(e) => {
                        const updatedEyeOption = { ...eyeOption, macAddress: e.target.checked };
                        processSearch("eye", updatedEyeOption);
                      }}
                      name="macAddress"
                      sx={getDefaultCheckboxSx()}
                    />
                  }
                  label="MAC Address"
                />
                {
                  treeIndex !== -1 && (
                    <>
                      {/* Task: #2191, hide client information */}
                      {/* Clients */}
                      {/* <Typography sx={{ ...getDefaultTopologySx(), mt: 2, }}>Clients</Typography>
                      <FormControlLabel
                        sx={{ transform: "translateX(-4px)", }}
                        control={
                          <Checkbox
                            icon={<UnselectedIcon />}
                            checkedIcon={<SelectedIcon />}
                            checked={eyeOption.allClients}
                            onChange={(e) => {
                              const updatedEyeOption = { ...eyeOption, allClients: e.target.checked };

                              if (e.target.checked) {
                                updatedEyeOption.wireless = true;
                                updatedEyeOption.wired = true;
                              } else {
                                updatedEyeOption.wireless = false;
                                updatedEyeOption.wired = false;
                              }

                              processSearch("eye", updatedEyeOption);
                            }}
                            name="allClients"
                            sx={getDefaultCheckboxSx()}
                          />
                        }
                        label="All clients"
                      />
                      <FormControlLabel
                        sx={{ transform: "translateX(22px)", }}
                        control={
                          <Checkbox
                            icon={<UnselectedIcon />}
                            checkedIcon={<SelectedIcon />}
                            checked={eyeOption.wireless}
                            onChange={(e) => {
                              const updatedEyeOption = { ...eyeOption, wireless: e.target.checked };

                              if (e.target.checked && eyeOption.wired) {
                                updatedEyeOption.allClients = true;
                              } else {
                                updatedEyeOption.allClients = false;
                              }

                              processSearch("eye", updatedEyeOption);
                            }}
                            name="wireless"
                            sx={getDefaultCheckboxSx()}
                          />
                        }
                        label="Wireless"
                      />
                      <FormControlLabel
                        sx={{ transform: "translateX(22px)", }}
                        control={
                          <Checkbox
                            icon={<UnselectedIcon />}
                            checkedIcon={<SelectedIcon />}
                            checked={eyeOption.wired}
                            onChange={(e) => {
                              const updatedEyeOption = { ...eyeOption, wired: e.target.checked };

                              if (e.target.checked && eyeOption.wireless) {
                                updatedEyeOption.allClients = true;
                              } else {
                                updatedEyeOption.allClients = false;
                              }

                              processSearch("eye", updatedEyeOption);
                            }}
                            name="wired"
                            sx={getDefaultCheckboxSx()}
                          />
                        }
                        label="Wired"
                      />
                      <FormControlLabel
                        sx={{ transform: "translateX(-4px)", }}
                        control={
                          <Checkbox
                            icon={<UnselectedIcon />}
                            checkedIcon={<SelectedIcon />}
                            checked={eyeOption.onlineClients}
                            onChange={(e) => {
                              const updatedEyeOption = { ...eyeOption, onlineClients: e.target.checked };
                              processSearch("eye", updatedEyeOption);
                            }}
                            name="onlineClients"
                            sx={getDefaultCheckboxSx()}
                          />
                        }
                        label="Online clients"
                      />
                      <FormControlLabel
                        sx={{ transform: "translateX(-4px)", }}
                        control={
                          <Checkbox
                            icon={<UnselectedIcon />}
                            checkedIcon={<SelectedIcon />}
                            checked={eyeOption.offlineClients}
                            onChange={(e) => {
                              const updatedEyeOption = { ...eyeOption, offlineClients: e.target.checked };
                              processSearch("eye", updatedEyeOption);
                            }}
                            name="offlineClients"
                            sx={getDefaultCheckboxSx()}
                          />
                        }
                        label="Offline clients"
                      /> */}
                    </>
                  )
                }
              </FormGroup>
            </Box>
            <Box sx={{ mt: 2, mb: 2, display: "flex", justifyContent: "center", alignItems: "center", gap: "8px" }}>
              <div
                className={toolbarStyle['toolbar-cancel-btn']}
                style={{ width: 'auto' }}
                onClick={() => {
                  const updatedEyeOption = cloneDeep(defaultEyeOption);

                  for (const key in updatedEyeOption) {
                    updatedEyeOption[key] = false;
                  }

                  setEyeOption(updatedEyeOption);
                  processSearch("eye", updatedEyeOption);
                }}
              >
                <span className={toolbarStyle['text']}>Reset</span>
              </div>
              <div
                className={toolbarStyle['toolbar-btn']}
                style={{ width: 'auto' }}
                onClick={() => {
                  const updatedEyeOption = cloneDeep(defaultEyeOption);
                  setEyeOption(updatedEyeOption);
                  processSearch("eye", updatedEyeOption);
                }}
              >
                <span className={toolbarStyle['text']}>Select All</span>
              </div>
            </Box>
          </Menu>
        </Box>
        {/* Export */}
        {/* Hide this*/}
        {/* <Box
          onClick={() => { }}>
          <div className={toolbarStyle['toolbar-btn']}>
            <img src={icn_btn_export} alt="Export" />
            <span className={toolbarStyle['text']}>Export</span>
          </div>
        </Box> */}
      </Box>
    </Box >
  );
};

export default Toolbar;